import { faAdd, faAngleDown, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, ListGroup, Row, Stack } from "react-bootstrap";
import { postAssessmentType } from "../../../services/httpClient";
import { unsavedChanges } from "../../../App";
import Input from "../../ui/Input";
import SelectObject from "../../ui/SelectObject";
import AssessmentQuestionItem from "./AssessmentQuestionItem";
import QuestionPreview from "./QuestionPreview";
import FormCheck from "../../ui/FormCheckbox";

interface AssessmentState {
  getAssessTypes: any;
  answerScales: any;
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  setShowCreateType: any;
  setIsLoading: any;
  setErrMsg: any;
}

const CreateAssessmentCard = ({
  getAssessTypes,
  answerScales,
  setOrgToSave,
  setShowToast,
  setToastText,
  setToastBody,
  setShowCreateType,
  setIsLoading,
  setErrMsg,
}: AssessmentState) => {
  const [allowSaveAssessment, setAllowSaveAssessment] = useState<boolean>(false);
  const [assessmentName, setAssessmentName] = useState<string>();
  const [frequency, setFrequency] = useState<number>();
  const [selectedScale, setSelectedScale] = useState<number>(0);
  const [enableAddQuestion, setEnableAddQuestion] = useState<boolean>(false);
  const [newQuestion, setNewQuestion] = useState<string>("");
  const [likertValues, setLikertValues] = useState<any[]>([]);
  const [useDefaultLikertValues, setUseDefaultLikertValues] = useState<boolean>(false);
  const [questions, setQuestions] = useState<any[]>([]);
  const [showAddQuestion, setShowAddQuestion] = useState<boolean>(false);
  const [showAnswerKeyValues, setShowAnswerKeyValues] = useState<boolean>(false);
  const [showQuestions, setShowQuestions] = useState<boolean>(false);
  const [showQuestionPreview, setShowQuestionPreview] = useState<boolean>(false);

  const frequencyOptions = [
    { key: 7, name: "Weekly" },
    { key: 14, name: "Every 2 Weeks" },
    { key: 30, name: "Monthly" },
    { key: 90, name: "Every 3 Months" },
    { key: 180, name: "Every 6 Months" },
    { key: 365, name: "Yearly" },
  ];

  const resetLikertValues = (scaleId: number) => {
    if (scaleId == 0) {
      setLikertValues([]);
    } else {
      const fieldSet = answerScales.filter((a: any) => a.id == scaleId);
      setLikertValues(fieldSet[0].fields);
    }
  };

  const updateAssessmentName = (e: any) => {
    setAssessmentName(e.target.value);

    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const updateScale = (e: any) => {
    setSelectedScale(e.target.value as number);
    resetLikertValues(e.target.value as number);
    setShowQuestionPreview(true);

    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const onClickShowAddQuestion = () => {
    setNewQuestion("");
    if (!useDefaultLikertValues) {
      resetLikertValues(selectedScale);
    }
    setShowAddQuestion(true);
  };

  const updateQuestion = (e: any) => {
    setNewQuestion(e.target.value);
    setEnableAddQuestion(true);
    setShowQuestionPreview(true);
  };

  const onClickAddQuestion = () => {
    setQuestions((prev) => [...prev, { question: newQuestion, fields: likertValues }]);

    setEnableAddQuestion(false);
    setNewQuestion("");

    if (!useDefaultLikertValues) {
      resetLikertValues(selectedScale);
    }

    setShowQuestions(true);
    setShowAnswerKeyValues(false);
    setShowQuestionPreview(false);

    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const onClickCancelQuestion = () => {
    setErrMsg("");
    setNewQuestion("");
    if (!useDefaultLikertValues) {
      resetLikertValues(selectedScale);
    }

    setShowAddQuestion(false);
    setEnableAddQuestion(false);
    setShowQuestionPreview(false);
  };

  useEffect(() => {
    if (assessmentName && questions.length > 0 && selectedScale > 0) {
      setAllowSaveAssessment(true);
    } else {
      setAllowSaveAssessment(false);
    }
  }, [assessmentName, questions, selectedScale]);

  const onClickCustomKeys = () => {
    if (selectedScale) {
      setShowAnswerKeyValues(true);
    }
  };

  const updateAnswerKey = (e: any, index: number) => {
    setLikertValues((prev) => prev.map((item) => (item.value === index ? { ...item, value_description: e.target.value } : item)));
  };

  const onClickSave = (publish = false) => {
    setErrMsg("");
    //get only the custom fields
    const filteredQuestions = questions.map((question) => ({
      ...question,
      fields: question.fields.filter((field: any) => field.value_description != field.value),
    }));

    let newType: any = {
      name: assessmentName,
      frequency: frequency,
      questions: filteredQuestions.map((q: any, index: number) => ({
        key: index + 1,
        question: q?.question,
        field_set_id: selectedScale,
        fields: q?.fields,
        order: index + 1,
      })),
    };

    if (publish) {
      newType = { ...newType, is_published: true };
    }

    setIsLoading(true);
    postAssessmentType(newType)
      .then((res: any) => {
        setAllowSaveAssessment(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        // trackFeatureEvent("button_click", "create_custom_assessment");
        getAssessTypes(publish);
        setIsLoading(false);
        setShowCreateType(false);
        setShowToast(true);
        setToastText("Assessment Created");
        setToastBody(assessmentName);
      })
      .catch((err: any) => {
        setIsLoading(false);
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const updateDefault = () => {
    setUseDefaultLikertValues(!useDefaultLikertValues);
  };

  const onClickQuestionSubmit = (e: any) => {
    e?.preventDefault();
    onClickAddQuestion();
  };

  return (
    <>
      <Card className="bg-light mb-3">
        <Card.Body>
          <Row className="mb-3">
            <Col xs={9}>
              <Input
                label="Assessment Name"
                type="textarea"
                value={assessmentName}
                onUpdate={(e: any) => updateAssessmentName(e)}
                hasFocus={true}
              />
            </Col>
            <Col xs={3}>
              <SelectObject
                label="Frequency"
                value={frequency}
                options={frequencyOptions}
                onUpdate={(e: any) => setFrequency(e.target.value)}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={12}>
              <SelectObject label="Answer Scale" value={selectedScale} options={answerScales} onUpdate={(e: any) => updateScale(e)} />
            </Col>
          </Row>
          {questions?.length > 0 && (
            <>
              <Row>
                <h6 className="cursor-pointer ms-1" onClick={() => setShowQuestions(!showQuestions)}>
                  Questions <FontAwesomeIcon icon={!showQuestions ? faAngleRight : faAngleDown} className="ms-2" />
                </h6>
              </Row>
              {showQuestions && (
                <Row className="mb-2 mx-0">
                  <ListGroup className="px-0">
                    {questions.map((q: any, index: number) => (
                      <AssessmentQuestionItem
                        key={index}
                        question={q}
                        index={index}
                        setQuestions={setQuestions}
                        setShowAddQuestion={setShowAddQuestion}
                        setErrMsg={setErrMsg}
                      />
                    ))}
                  </ListGroup>
                </Row>
              )}
            </>
          )}
          <Row className="mb-0">
            {showAddQuestion ? (
              <Col xs={12}>
                <h6 className="cursor-pointer ms-1">Create New Question</h6>
                <Form onSubmit={(e: any) => onClickQuestionSubmit(e)}>
                  <Stack direction="horizontal" gap={3}>
                    <Stack style={{ justifyContent: "center" }} className="col-6">
                      <Input label="Question" type="text" value={newQuestion} onUpdate={(e: any) => updateQuestion(e)} />
                    </Stack>
                    <Button
                      type="submit"
                      className="app-secondary-color button"
                      disabled={!enableAddQuestion}
                      onClick={() => onClickAddQuestion()}
                    >
                      Add
                    </Button>
                    <div className="vr" />
                    <Button className="app-primary-color button" onClick={() => onClickCancelQuestion()}>
                      Cancel
                    </Button>
                  </Stack>
                </Form>
                {selectedScale > 0 &&
                  (!showAnswerKeyValues ? (
                    <h6 className="app-tertiary-color-inverse cursor-pointer bg-light mt-2 mb-3 ms-1" onClick={() => onClickCustomKeys()}>
                      {"Edit Answer Key Descriptions"}
                    </h6>
                  ) : (
                    <Stack direction="horizontal" className="mt-3">
                      <h6 className="ms-1 me-3">Answer Key Descriptions</h6>
                      <FormCheck label="Set as Default" value={useDefaultLikertValues} onUpdate={(e: any) => updateDefault()} />
                    </Stack>
                  ))}

                {showAnswerKeyValues && (
                  <Stack style={{ justifyContent: "center" }} className="col-12" gap={3}>
                    {[...Array(likertValues.length)].map((_, index) => (
                      <Stack direction="horizontal" gap={3} className="col-8">
                        <Col>
                          <Input
                            label="Answer Key Value"
                            type="text"
                            value={likertValues[index]?.value_description}
                            onUpdate={(e: any) => updateAnswerKey(e, index)}
                          />
                        </Col>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Col>
            ) : (
              <>
                <Col xs={6}>
                  <p className="mt-2">{questions.length > 0 ? "" : "No Questions Added"}</p>
                </Col>
                <Col xs={6} className="text-end">
                  <Button className="app-tertiary-color button" disabled={selectedScale === 0} onClick={() => onClickShowAddQuestion()}>
                    <FontAwesomeIcon icon={faAdd} />
                  </Button>
                </Col>
              </>
            )}
          </Row>
        </Card.Body>
      </Card>
      {showQuestionPreview && <QuestionPreview qIndex={questions.length} question={newQuestion} likertValues={likertValues} />}
      <Row className="mb-5">
        <Col className="text-center">
          <Stack direction="horizontal" gap={3} className="justify-content-center">
            <Button className="button app-secondary-color button" onClick={() => onClickSave(false)} disabled={!allowSaveAssessment}>
              Save Draft
            </Button>
            <div className="vr mb-1" />
            <Button className="button app-tertiary-color button" onClick={() => onClickSave(true)} disabled={!allowSaveAssessment}>
              Publish
            </Button>
          </Stack>
        </Col>
      </Row>
    </>
  );
};

export default CreateAssessmentCard;
